'use client'

import * as React from 'react'
import {
  BlocksRenderer,
  type BlocksContent,
} from '@strapi/blocks-react-renderer'
import {
  isStrapiBlocksRootContent,
  pickBestUrlFromStrapiFile,
  rewriteHtmlExternalLinksToNewTab,
  rewriteHtmlUploadSrcToAbsolute,
} from '@/lib/marketplace-services'
import { cn } from '@/lib/utils'

/**
 * Shared shell: Strapi Blocks + HTML (markdown) output.
 * Explicit [&_*] rules so styling works without @tailwindcss/typography.
 */
const richTextShell = cn(
  'strapi-rich-text max-w-none',
  'text-[15px] sm:text-base leading-relaxed text-foreground',

  /* Headings */
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:tracking-tight [&_h1]:text-foreground sm:[&_h1]:text-3xl',
  '[&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:first:mt-0',
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground sm:[&_h2]:text-2xl',
  '[&_h2]:mt-7 [&_h2]:mb-2.5 [&_h2]:first:mt-0',
  '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:tracking-tight [&_h3]:text-foreground sm:[&_h3]:text-xl',
  '[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:first:mt-0',
  '[&_h4]:text-base [&_h4]:font-semibold [&_h4]:text-foreground sm:[&_h4]:text-lg',
  '[&_h4]:mt-5 [&_h4]:mb-2 [&_h4]:first:mt-0',
  '[&_h5]:text-sm [&_h5]:font-semibold [&_h5]:uppercase [&_h5]:tracking-wide [&_h5]:text-muted-foreground sm:[&_h5]:text-base',
  '[&_h5]:mt-5 [&_h5]:mb-1.5 [&_h5]:first:mt-0',
  '[&_h6]:text-sm [&_h6]:font-semibold [&_h6]:text-muted-foreground',
  '[&_h6]:mt-4 [&_h6]:mb-1.5 [&_h6]:first:mt-0',

  /* Paragraphs */
  '[&_p]:mb-3 [&_p]:last:mb-0 [&_p]:text-foreground/90',

  /* Links */
  '[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-[3px] [&_a]:decoration-primary/35',
  '[&_a]:transition-colors hover:[&_a]:text-primary/85 hover:[&_a]:decoration-primary/60',
  'dark:[&_a]:text-white dark:[&_a]:decoration-white/40',
  'dark:hover:[&_a]:text-white/90 dark:hover:[&_a]:decoration-white/70',

  /* Lists */
  '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul]:text-foreground/90',
  '[&_ul]:marker:text-primary dark:[&_ul]:marker:text-white',
  '[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5 [&_ol]:text-foreground/90',
  '[&_ol]:marker:font-semibold [&_ol]:marker:text-primary dark:[&_ol]:marker:text-white',
  '[&_li]:pl-1 [&_li]:leading-relaxed',
  '[&_li_>_ul]:my-2 [&_li_>_ol]:my-2',

  /* Blockquote */
  '[&_blockquote]:my-5 [&_blockquote]:rounded-r-xl [&_blockquote]:border-l-4 [&_blockquote]:border-primary/45',
  '[&_blockquote]:bg-muted/35 [&_blockquote]:py-3 [&_blockquote]:pl-4 [&_blockquote]:pr-4',
  '[&_blockquote]:text-muted-foreground [&_blockquote]:italic',

  /* Horizontal rule */
  '[&_hr]:my-8 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-border',

  /* Inline & block code */
  '[&_p>code]:rounded-md [&_p>code]:bg-primary/10 [&_p>code]:px-1.5 [&_p>code]:py-0.5',
  '[&_p>code]:font-mono [&_p>code]:text-[0.875em] [&_p>code]:text-foreground',
  '[&_li>code]:rounded-md [&_li>code]:bg-primary/10 [&_li>code]:px-1.5 [&_li>code]:py-0.5',
  '[&_li>code]:font-mono [&_li>code]:text-[0.875em]',
  '[&_pre]:my-5 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-border',
  '[&_pre]:bg-muted/45 [&_pre]:p-4 dark:[&_pre]:bg-muted/25',
  '[&_pre_code]:block [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:font-mono',
  '[&_pre_code]:text-[13px] [&_pre_code]:leading-relaxed [&_pre_code]:text-foreground',

  /* Emphasis */
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_em]:italic [&_em]:text-foreground/95',

  /* Tables (GFM) */
  '[&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-lg',
  '[&_table]:border [&_table]:border-border [&_table]:text-sm',
  '[&_thead]:bg-muted/50',
  '[&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground',
  '[&_td]:border-b [&_td]:border-border/80 [&_td]:px-3 [&_td]:py-2.5 [&_td]:text-foreground/90',
  '[&_tr:last-child_td]:border-b-0',

  /* Images & figures */
  '[&_img]:my-6 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-xl [&_img]:border [&_img]:border-border/70',
  '[&_img]:shadow-sm',
  '[&_figure]:my-6 [&_figure]:mx-auto [&_figure]:max-w-full',
  '[&_figcaption]:mt-2 [&_figcaption]:text-center [&_figcaption]:text-xs [&_figcaption]:text-muted-foreground',
)

const linkClassName =
  'font-medium text-primary underline underline-offset-[3px] decoration-primary/35 transition-colors hover:text-primary/85 hover:decoration-primary/60 dark:text-white dark:decoration-white/40 dark:hover:text-white/90 dark:hover:decoration-white/70'

export function ServiceAddressesBody({
  html,
  blocks,
  className,
}: {
  html: string | null | undefined
  blocks: unknown[] | null | undefined
  className?: string
}) {
  if (blocks?.length && isStrapiBlocksRootContent(blocks)) {
    return (
      <div className={cn(richTextShell, className)}>
        <BlocksRenderer
          content={blocks as BlocksContent}
          blocks={{
            paragraph: (props) => (
              <p className="mb-3 text-foreground/90 last:mb-0 leading-relaxed">
                {props.children}
              </p>
            ),
            heading: ({ level, children }) => {
              const styles: Record<number, string> = {
                1: 'mt-8 mb-3 text-2xl font-bold tracking-tight text-foreground first:mt-0 sm:text-3xl',
                2: 'mt-7 mb-2.5 text-xl font-semibold tracking-tight text-foreground first:mt-0 sm:text-2xl',
                3: 'mt-6 mb-2 text-lg font-semibold tracking-tight text-foreground first:mt-0 sm:text-xl',
                4: 'mt-5 mb-2 text-base font-semibold text-foreground first:mt-0 sm:text-lg',
                5: 'mt-5 mb-1.5 text-sm font-semibold uppercase tracking-wide text-muted-foreground first:mt-0 sm:text-base',
                6: 'mt-4 mb-1.5 text-sm font-semibold text-muted-foreground first:mt-0',
              }
              const Tag = `h${level}` as const
              return <Tag className={styles[level] ?? styles[4]}>{children}</Tag>
            },
            quote: (props) => (
              <blockquote className="my-5 rounded-r-xl border-l-4 border-primary/45 bg-muted/35 py-3 pl-4 pr-4 italic text-muted-foreground">
                {props.children}
              </blockquote>
            ),
            code: (props) => (
              <pre className="my-5 overflow-x-auto rounded-xl border border-border bg-muted/45 p-4 dark:bg-muted/25">
                <code className="block font-mono text-[13px] leading-relaxed text-foreground">
                  {props.plainText}
                </code>
              </pre>
            ),
            list: (props) =>
              props.format === 'ordered' ? (
                <ol className="my-4 list-decimal space-y-2 pl-5 text-foreground/90 marker:font-semibold marker:text-primary dark:marker:text-white">
                  {props.children}
                </ol>
              ) : (
                <ul className="my-4 list-disc space-y-2 pl-5 text-foreground/90 marker:text-primary dark:marker:text-white">
                  {props.children}
                </ul>
              ),
            'list-item': (props) => (
              <li className="pl-1 leading-relaxed [&_ul]:my-2 [&_ol]:my-2">{props.children}</li>
            ),
            link: (props) => {
              const url = props.url ?? '#'
              const external = /^https?:\/\//i.test(url)
              return (
                <a
                  href={url}
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className={linkClassName}
                >
                  {props.children}
                </a>
              )
            },
            image: (props) => {
              const src = pickBestUrlFromStrapiFile(props.image)
              if (!src) return null
              const img = props.image as {
                alternativeText?: string | null
                width?: number
                height?: number
              }
              return (
                <figure className="my-6 mx-auto max-w-full">
                  {React.createElement('img', {
                    src,
                    alt: img?.alternativeText ?? '',
                    width: img?.width,
                    height: img?.height,
                    className:
                      'h-auto max-w-full rounded-xl border border-border/70 shadow-sm',
                    loading: 'lazy',
                    decoding: 'async',
                  })}
                </figure>
              )
            },
          }}
        />
      </div>
    )
  }

  if (html) {
    const safe = rewriteHtmlExternalLinksToNewTab(rewriteHtmlUploadSrcToAbsolute(html))
    return (
      <div
        className={cn(richTextShell, className)}
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    )
  }

  return null
}
